// src/components/operationCenter/widgets/index.ts
// Widget Registry - Maps widget types to their components and configurations

import {
  TrendingUp,
  Bookmark,
  LineChart,
  BarChart3,
  PieChart,
  Gauge,
  MessageSquare,
  Newspaper,
  Activity,
  DollarSign,
  Target,
  Layers,
  LayoutGrid,
  Briefcase,
  Banknote,
  Gem,
  StickyNote,
  Table2,
  Brain,
  CircleDollarSign,
  TrendingDown,
  Globe,
  ListChecks,
  Percent,
  Calendar,
  Zap,
  Scale,
  Workflow,
  Users,
  HeartPulse,
  LayoutDashboard,
  AlertTriangle,
  FileCheck,
  FlaskConical,
} from "lucide-react";
import type {
  WidgetDefinition,
  WidgetType,
  WidgetCategory,
} from "@/types/dashboard";

// Import widget components
import TopMoversWidget from "./TopMoversWidget";
import WatchlistWidget from "./WatchlistWidget";
import TokenMetricsCardWidget from "./TokenMetricsCardWidget";
import TweetFeedWidget from "./TweetFeedWidget";
import NewsFeedWidget from "./NewsFeedWidget";
import FearGreedWidget from "./FearGreedWidget";
import FundingRateWidget from "./FundingRateWidget";
import OpenInterestWidget from "./OpenInterestWidget";
import TradingViewWidget from "./TradingViewWidget";
import TimeSeriesChartWidget from "./TimeSeriesChartWidget";
import PortfolioNavWidget from "./PortfolioNavWidget";
import PlaceholderWidget from "./PlaceholderWidget";
import MarketPulseWidget from "./MarketPulseWidget";
import ETFFlowsWidget from "./ETFFlowsWidget";
import PortfolioHoldingsWidget from "./PortfolioHoldingsWidget";
import PortfolioAllocationWidget from "./PortfolioAllocationWidget";
import TreemapWidget from "./TreemapWidget";
import BarChartWidget from "./BarChartWidget";
import ScatterChartWidget from "./ScatterChartWidget";
import FXMoversWidget from "./FXMoversWidget";
import CommoditiesMoversWidget from "./CommoditiesMoversWidget";
import NotepadWidget from "./NotepadWidget";
import SpreadsheetWidget from "./SpreadsheetWidget";
import DailyRecapWidget from "./DailyRecapWidget";
import SectorNarrativesWidget from "./SectorNarrativesWidget";
import YouTubeAlphaWidget from "./YouTubeAlphaWidget";

// BTC On-Chain/Cycle Indicator Widgets
import AHR999Widget from "./AHR999Widget";
import PiCycleTopWidget from "./PiCycleTopWidget";
import GlobalM2Widget from "./GlobalM2Widget";
import BullMarketIndicatorsWidget from "./BullMarketIndicatorsWidget";
import PuellMultipleWidget from "./PuellMultipleWidget";
import GoldenRatioMultiplierWidget from "./GoldenRatioMultiplierWidget";
import StockToFlowWidget from "./StockToFlowWidget";
import HolderRealizedPriceWidget from "./HolderRealizedPriceWidget";

// Macro Indicator Widgets
import FedRateProbabilitiesWidget from "./FedRateProbabilitiesWidget";
import EconomicCalendarWidget from "./EconomicCalendarWidget";
import MacroKeyMetricsWidget from "./MacroKeyMetricsWidget";

// Chart Widgets (from TokenScreener)
import CorrelationMatrixWidget from "./CorrelationMatrixWidget";
import HeatmapWidget from "./HeatmapWidget";

// Portfolio Intelligence Widgets
import PortfolioPulseWidget from "./PortfolioPulseWidget";
import PortfolioMetricsOverviewWidget from "./PortfolioMetricsOverviewWidget";
import ThresholdMonitorWidget from "./ThresholdMonitorWidget";

// Thesis Widgets
import { ThesisWidget } from "./thesis";

// Generic endpoint-based widgets
import GenericChartWidget from "./GenericChartWidget";
import GenericMetricCardWidget from "./GenericMetricCardWidget";
import GenericStatTableWidget from "./GenericStatTableWidget";
import GenericEndpointBarChartWidget from "./GenericEndpointBarChartWidget";
import GenericPieChartWidget from "./GenericPieChartWidget";
import GenericCandlestickWidget from "./GenericCandlestickWidget";

// Token Dashboard Widgets (tw_*)
import TwKeyStatsWidget from "./token/overview/TwKeyStatsWidget";
import TwPerformanceStripWidget from "./token/overview/TwPerformanceStripWidget";
import TwRiskEfficiencyWidget from "./token/overview/TwRiskEfficiencyWidget";
import TwMarketRelationshipsWidget from "./token/overview/TwMarketRelationshipsWidget";
import TwSectorHeatWidget from "./token/overview/TwSectorHeatWidget";
import TwFundingTrendWidget from "./token/overview/TwFundingTrendWidget";
import TwOrderBookWidget from "./token/overview/TwOrderBookWidget";
import TwSupplyUnlocksWidget from "./token/overview/TwSupplyUnlocksWidget";
import TwMovingAveragesWidget from "./token/performance/TwMovingAveragesWidget";
import TwVsBenchmarksWidget from "./token/performance/TwVsBenchmarksWidget";
import TwPerformanceQualityWidget from "./token/performance/TwPerformanceQualityWidget";
import TwSeasonalPerformanceWidget from "./token/performance/TwSeasonalPerformanceWidget";
import TwStreaksEfficiencyWidget from "./token/performance/TwStreaksEfficiencyWidget";
import TwRollingVolatilityWidget from "./token/risk/TwRollingVolatilityWidget";
import TwSharpeSortinoWidget from "./token/risk/TwSharpeSortinoWidget";
import TwVarEsWidget from "./token/risk/TwVarEsWidget";
import TwSkewKurtosisWidget from "./token/risk/TwSkewKurtosisWidget";
import TwUpsideDownsideWidget from "./token/risk/TwUpsideDownsideWidget";
import TwTailMetricsWidget from "./token/risk/TwTailMetricsWidget";
import TwBetaCorrelationWidget from "./token/risk/TwBetaCorrelationWidget";
import TwFeesDynamicsWidget from "./token/valuation/TwFeesDynamicsWidget";
import TwTvlDynamicsWidget from "./token/valuation/TwTvlDynamicsWidget";
import TwDauDynamicsWidget from "./token/valuation/TwDauDynamicsWidget";
import TwMcFeesHistoryWidget from "./token/valuation/TwMcFeesHistoryWidget";
import TwMcTvlHistoryWidget from "./token/valuation/TwMcTvlHistoryWidget";
import TwEfficiencyPanelWidget from "./token/valuation/TwEfficiencyPanelWidget";
import TwValuationInsightsWidget from "./token/valuation/TwValuationInsightsWidget";
import TwValuationRatiosWidget from "./token/valuation/TwValuationRatiosWidget";
import TwOiSummaryWidget from "./token/derivatives/TwOiSummaryWidget";
import TwOiChartWidget from "./token/derivatives/TwOiChartWidget";
import TwFundingSummaryWidget from "./token/derivatives/TwFundingSummaryWidget";
import TwWeightedFundingChartWidget from "./token/derivatives/TwWeightedFundingChartWidget";
import TwExchangeFundingWidget from "./token/derivatives/TwExchangeFundingWidget";
import TwRegimeWidget from "./token/derivatives/TwRegimeWidget";
import TwFundingExtremesWidget from "./token/derivatives/TwFundingExtremesWidget";
import TwFundingRateChartWidget from "./token/derivatives/TwFundingRateChartWidget";

// Signal Widgets
import SignalChartWidget from "./SignalChartWidget";
import SpreadRatioWidget from "./SpreadRatioWidget";
import ZScoreWidget from "./ZScoreWidget";
import StationarityWidget from "./StationarityWidget";
import { SIGNAL_DEFINITIONS } from "@/constants/signals";

// ─────────────────────────────────────────────────────────────────────────────
// Standard Widget Sizes
// Default sizes must use: w = 3, 6, 9, or 12 | h = 4 or 8
// Min/max sizes can be any value
// ─────────────────────────────────────────────────────────────────────────────

export const WIDGET_SIZES = {
  // Quarter width (3 cols)
  QUARTER_SHORT: { w: 3, h: 4 },
  QUARTER_TALL: { w: 3, h: 8 },
  // Half width (6 cols)
  HALF_SHORT: { w: 6, h: 4 },
  HALF_TALL: { w: 6, h: 8 },
  // Three-quarter width (9 cols)
  THREE_QUARTER_SHORT: { w: 9, h: 4 },
  THREE_QUARTER_TALL: { w: 9, h: 8 },
  // Full width (12 cols)
  FULL_SHORT: { w: 12, h: 4 },
  FULL_TALL: { w: 12, h: 8 },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Signal Widget Entries (programmatically generated from SIGNAL_DEFINITIONS)
// ─────────────────────────────────────────────────────────────────────────────

const SIGNAL_WIDGET_ENTRIES = Object.fromEntries(
  SIGNAL_DEFINITIONS.map((sig) => [
    sig.widgetType,
    {
      type: sig.widgetType,
      component: SignalChartWidget,
      label: sig.label,
      description: sig.description,
      icon: Activity,
      category: "signals" as const,
      sizeCategory: "small" as const,
      defaultSize: WIDGET_SIZES.QUARTER_SHORT,
      minSize: { w: 3, h: 4 },
      maxSize: { w: 6, h: 8 },
      defaultConfig: { signal_name: sig.signalName },
      configurable: false,
      hasInternalHeader: true,
    },
  ]),
) as Record<string, WidgetDefinition>;

// ─────────────────────────────────────────────────────────────────────────────
// Widget Registry
// ─────────────────────────────────────────────────────────────────────────────

export const WIDGET_REGISTRY = {
  // ═══════════════════════════════════════════════════════════════════════════
  // MARKET INTELLIGENCE
  // Token market data, watchlists, and market overview
  // ═══════════════════════════════════════════════════════════════════════════
  top_movers: {
    type: "top_movers",
    component: TopMoversWidget,
    label: "Top Movers",
    description: "Display top gainers or losers by price change",
    icon: TrendingUp,
    category: "market",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      direction: "gainers",
      time_range: "1d",
      limit: 10,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  watchlist: {
    type: "watchlist",
    component: WatchlistWidget,
    label: "Watchlist",
    description: "Track your favorite tokens",
    icon: Bookmark,
    category: "market",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      tickers: ["BTC", "ETH", "SOL"],
    },
    configurable: true,
    hasInternalHeader: true,
  },

  market_pulse: {
    type: "market_pulse",
    component: MarketPulseWidget,
    label: "Market Pulse",
    description: "Real-time market summary",
    icon: Activity,
    category: "market",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  fear_greed: {
    type: "fear_greed",
    component: FearGreedWidget,
    label: "Fear & Greed",
    description: "Crypto market sentiment index",
    icon: Gauge,
    category: "market",
    sizeCategory: "small",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      show_gauge: true,
      show_history: false,
    },
    configurable: true,
    hasInternalHeader: false,
  },

  sector_narratives: {
    type: "sector_narratives",
    component: SectorNarrativesWidget,
    label: "Sector Narratives",
    description: "Hot narratives by sector",
    icon: Layers,
    category: "market",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARTS & VISUALIZATION
  // Generic charting tools for token analysis
  // ═══════════════════════════════════════════════════════════════════════════
  time_series_chart: {
    type: "time_series_chart",
    component: TimeSeriesChartWidget,
    label: "Multi-Token Chart",
    description: "Compare multiple tokens across metrics",
    icon: LineChart,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      tickers: ["BTC", "ETH"],
      metric: "price_close",
      timeframe: "30d",
      style: "line",
      show_legend: true,
      index_to_100: true,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  bar_chart: {
    type: "bar_chart",
    component: BarChartWidget,
    label: "Ranking Chart",
    description: "Rank tokens by any metric with sorting controls",
    icon: BarChart3,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      metric: "price_close",
      sort: "desc",
      limit: 25,
      color_by_value: true,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  scatter_chart: {
    type: "scatter_chart",
    component: ScatterChartWidget,
    label: "Scatter Plot",
    description: "Visualize correlation between X/Y metrics",
    icon: Target,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      x_metric: "market_cap",
      y_metric: "volume",
      show_labels: true,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  treemap: {
    type: "treemap",
    component: TreemapWidget,
    label: "Treemap",
    description: "Market cap weighted visualization",
    icon: LayoutGrid,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      size_metric: "market_cap",
      color_metric: "price_close",
      limit: 30,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  heatmap: {
    type: "heatmap",
    component: HeatmapWidget,
    label: "Heatmap",
    description:
      "Time-series heatmap with transforms (momentum, vol, dominance)",
    icon: PieChart,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      metric: "price_close",
      time_range: "30d",
      transform: "raw",
      window_days: 7,
      clustered: false,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  token_metrics_card: {
    type: "token_metrics_card",
    component: TokenMetricsCardWidget,
    label: "Token Metrics Card",
    description:
      "Mini dashboard for a single token with sparkline, fundamentals, and derivatives",
    icon: Activity,
    category: "market",
    sizeCategory: "small",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      ticker: "BTC",
    },
    configurable: true,
    hasInternalHeader: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PORTFOLIO
  // Portfolio tracking and analysis
  // ═══════════════════════════════════════════════════════════════════════════
  portfolio_nav: {
    type: "portfolio_nav",
    component: PortfolioNavWidget,
    label: "My Portfolio NAV",
    description: "Track portfolio value and performance vs BTC",
    icon: LineChart,
    category: "portfolio",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      timeframe: "all",
      show_benchmark: false,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  portfolio_holdings: {
    type: "portfolio_holdings",
    component: PortfolioHoldingsWidget,
    label: "My Holdings",
    description: "View current portfolio positions",
    icon: Briefcase,
    category: "portfolio",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  portfolio_allocation: {
    type: "portfolio_allocation",
    component: PortfolioAllocationWidget,
    label: "Portfolio Allocation",
    description: "Asset allocation breakdown",
    icon: PieChart,
    category: "portfolio",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  correlation_matrix: {
    type: "correlation_matrix",
    component: CorrelationMatrixWidget,
    label: "Correlation Matrix",
    description: "Top tokens correlation matrix by returns",
    icon: Target,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      metric: "price_close",
      time_range: "7d",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  portfolio_pulse: {
    type: "portfolio_pulse",
    component: PortfolioPulseWidget,
    label: "Portfolio Pulse",
    description: "Quick overview: P&L, top movers, health score",
    icon: HeartPulse,
    category: "portfolio",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      time_range: "7d",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  portfolio_metrics_overview: {
    type: "portfolio_metrics_overview",
    component: PortfolioMetricsOverviewWidget,
    label: "Portfolio Metrics",
    description: "Categorized portfolio metrics and stats",
    icon: LayoutDashboard,
    category: "portfolio",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  risk_alerts: {
    type: "risk_alerts",
    component: ThresholdMonitorWidget,
    label: "Threshold Monitor",
    description: "Monitor alert policies and their status",
    icon: AlertTriangle,
    category: "portfolio",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TECHNICAL ANALYSIS
  // Price charts and technical indicators
  // ═══════════════════════════════════════════════════════════════════════════
  price_overview: {
    type: "price_overview",
    component: PlaceholderWidget,
    label: "Price Overview",
    description: "Current price with technical indicators",
    icon: Activity,
    category: "technical",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      ticker: "BTC",
      show_technicals: true,
    },
    configurable: true,
  },

  support_resistance: {
    type: "support_resistance",
    component: PlaceholderWidget,
    label: "S/R Levels",
    description: "Key support and resistance levels",
    icon: Target,
    category: "technical",
    sizeCategory: "small",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      ticker: "BTC",
    },
    configurable: true,
  },

  tradingview_chart: {
    type: "tradingview_chart",
    component: TradingViewWidget,
    label: "TradingView Chart",
    description: "Full TradingView chart embed",
    icon: LineChart,
    category: "technical",
    sizeCategory: "full",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      ticker: "BTC",
      interval: "D",
      theme: "dark",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SOCIAL & SENTIMENT
  // News, social feeds, and sentiment analysis
  // ═══════════════════════════════════════════════════════════════════════════
  tweet_feed: {
    type: "tweet_feed",
    component: TweetFeedWidget,
    label: "Twitter Feed",
    description: "Filtered crypto tweets",
    icon: MessageSquare,
    category: "social",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      tickers: ["BTC", "ETH"],
      sentiment_filter: "all",
      limit: 20,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  news_feed: {
    type: "news_feed",
    component: NewsFeedWidget,
    label: "News Feed",
    description: "Latest crypto news",
    icon: Newspaper,
    category: "social",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      limit: 20,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  youtube_alpha: {
    type: "youtube_alpha",
    component: YouTubeAlphaWidget,
    label: "YouTube Alpha",
    description: "Recent crypto video content",
    icon: Activity,
    category: "social",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  daily_recap: {
    type: "daily_recap",
    component: DailyRecapWidget,
    label: "24h AI Recap",
    description: "AI-powered 24-hour analysis of news and social sentiment",
    icon: Brain,
    category: "social",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      tokens: [],
    },
    configurable: false,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DERIVATIVES
  // Funding rates, open interest, and derivatives data
  // ═══════════════════════════════════════════════════════════════════════════
  funding_rate: {
    type: "funding_rate",
    component: FundingRateWidget,
    label: "Funding Rate",
    description: "Historical funding rates with price overlay",
    icon: Activity,
    category: "derivatives",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      tickers: ["BTC"],
      show_weighted: true,
      dashboard_type: "layer1",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  open_interest: {
    type: "open_interest",
    component: OpenInterestWidget,
    label: "Open Interest",
    description: "Track OI trends with price overlay",
    icon: BarChart3,
    category: "derivatives",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      ticker: "BTC",
      dashboard_type: "layer1",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // BTC ON-CHAIN & CYCLE INDICATORS
  // Bitcoin-specific on-chain metrics and cycle timing indicators
  // ═══════════════════════════════════════════════════════════════════════════
  bull_market_indicators: {
    type: "bull_market_indicators",
    component: BullMarketIndicatorsWidget,
    label: "Bull Market Indicators",
    description: '"Sell at the top" indicator summary table',
    icon: ListChecks,
    category: "onchain",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  ahr999: {
    type: "ahr999",
    component: AHR999Widget,
    label: "AHR999 Index",
    description: "Bitcoin cycle indicator with buy/sell zones",
    icon: CircleDollarSign,
    category: "onchain",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  pi_cycle_top: {
    type: "pi_cycle_top",
    component: PiCycleTopWidget,
    label: "Pi Cycle Top",
    description: "MA crossover cycle top detection indicator",
    icon: TrendingDown,
    category: "onchain",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  puell_multiple: {
    type: "puell_multiple",
    component: PuellMultipleWidget,
    label: "Puell Multiple",
    description: "Mining profitability indicator for cycle tops/bottoms",
    icon: Zap,
    category: "onchain",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  golden_ratio_multiplier: {
    type: "golden_ratio_multiplier",
    component: GoldenRatioMultiplierWidget,
    label: "BTC Golden Ratio",
    description: "Bitcoin Fibonacci-based long-term valuation zones",
    icon: Scale,
    category: "onchain",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  stock_to_flow: {
    type: "stock_to_flow",
    component: StockToFlowWidget,
    label: "BTC Stock to Flow",
    description: "Bitcoin scarcity model with halving markers",
    icon: Workflow,
    category: "onchain",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  holder_realized_price: {
    type: "holder_realized_price",
    component: HolderRealizedPriceWidget,
    label: "BTC Realized Price",
    description: "Bitcoin cost basis for short/long-term holders",
    icon: Users,
    category: "onchain",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  global_m2: {
    type: "global_m2",
    component: GlobalM2Widget,
    label: "Global M2 vs BTC",
    description: "Global M2 liquidity vs BTC price correlation",
    icon: Globe,
    category: "onchain",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // MACRO & ECONOMICS
  // Traditional finance, FX, commodities, and economic indicators
  // ═══════════════════════════════════════════════════════════════════════════
  macro_key_metrics: {
    type: "macro_key_metrics",
    component: MacroKeyMetricsWidget,
    label: "Macro Indicators",
    description: "DXY, VIX, yields, M2, and other macro indicators",
    icon: Globe,
    category: "macro",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  etf_flows: {
    type: "etf_flows",
    component: ETFFlowsWidget,
    label: "ETF Flows",
    description: "Bitcoin ETF inflows/outflows",
    icon: DollarSign,
    category: "macro",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      ticker: "btc",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  fed_rate_probabilities: {
    type: "fed_rate_probabilities",
    component: FedRateProbabilitiesWidget,
    label: "Fed Rate Probabilities",
    description: "Market-implied Fed rate decision probabilities",
    icon: Percent,
    category: "macro",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  economic_calendar: {
    type: "economic_calendar",
    component: EconomicCalendarWidget,
    label: "Economic Calendar",
    description: "Upcoming and recent economic events",
    icon: Calendar,
    category: "macro",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: false,
    hasInternalHeader: true,
  },

  fx_movers: {
    type: "fx_movers",
    component: FXMoversWidget,
    label: "FX Strongest",
    description: "Strongest currencies vs USD",
    icon: Banknote,
    category: "macro",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      direction: "strongest",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  fx_movers_weakest: {
    type: "fx_movers_weakest",
    component: FXMoversWidget,
    label: "FX Weakest",
    description: "Weakest currencies vs USD",
    icon: Banknote,
    category: "macro",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      direction: "weakest",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  commodities_movers: {
    type: "commodities_movers",
    component: CommoditiesMoversWidget,
    label: "Commodities Gainers",
    description: "Top gainers in commodities",
    icon: Gem,
    category: "macro",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      direction: "gainers",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  commodities_movers_losers: {
    type: "commodities_movers_losers",
    component: CommoditiesMoversWidget,
    label: "Commodities Losers",
    description: "Top losers in commodities",
    icon: Gem,
    category: "macro",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      direction: "losers",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITY
  // Productivity tools
  // ═══════════════════════════════════════════════════════════════════════════
  notepad: {
    type: "notepad",
    component: NotepadWidget,
    label: "Notepad",
    description: "Personal notes that auto-save to local storage",
    icon: StickyNote,
    category: "utility",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {
      content: "",
    },
    configurable: false,
    hasInternalHeader: true,
  },

  spreadsheet: {
    type: "spreadsheet",
    component: SpreadsheetWidget,
    label: "Spreadsheet",
    description: "Excel-like table with formulas and auto-save",
    icon: Table2,
    category: "utility",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      data: [],
      rows: 12,
      cols: 8,
    },
    configurable: false,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // THESIS
  // Investment thesis tracking and validation
  // ═══════════════════════════════════════════════════════════════════════════
  thesis: {
    type: "thesis",
    component: ThesisWidget,
    label: "Thesis Tracker",
    description: "Track and validate your investment thesis",
    icon: FileCheck,
    category: "thesis",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 6, h: 8 },
    defaultConfig: {},
    configurable: true,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // GENERIC — configurable widgets for any backend endpoint
  // ═══════════════════════════════════════════════════════════════════════════
  generic_chart: {
    type: "generic_chart",
    component: GenericChartWidget,
    label: "Generic Line Chart",
    description: "Time series line chart from any backend endpoint",
    icon: LineChart,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      endpoint: "",
      title: "Chart",
      x_field: "timestamp",
      y_fields: ["value"],
      y_axis_format: "number",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  generic_metric_card: {
    type: "generic_metric_card",
    component: GenericMetricCardWidget,
    label: "Generic Metric Card",
    description: "Show 1-4 stat values with change % from any endpoint",
    icon: Activity,
    category: "charts",
    sizeCategory: "small",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      endpoint: "",
      title: "Metrics",
      metrics: [{ label: "Value", field: "value", format: "number" }],
    },
    configurable: true,
    hasInternalHeader: true,
  },

  generic_stat_table: {
    type: "generic_stat_table",
    component: GenericStatTableWidget,
    label: "Generic Data Table",
    description: "Scrollable table with configurable columns from any endpoint",
    icon: Table2,
    category: "charts",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      endpoint: "",
      title: "Table",
      columns: [{ key: "name", label: "Name", format: "number" }],
      limit: 50,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  generic_endpoint_bar_chart: {
    type: "generic_endpoint_bar_chart",
    component: GenericEndpointBarChartWidget,
    label: "Generic Bar Chart",
    description: "Ranked or categorical bar chart from any endpoint",
    icon: BarChart3,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      endpoint: "",
      title: "Bar Chart",
      label_field: "name",
      value_field: "value",
      sort: "desc",
      limit: 20,
      format: "number",
      horizontal: true,
      color_by_value: false,
    },
    configurable: true,
    hasInternalHeader: true,
  },

  generic_pie_chart: {
    type: "generic_pie_chart",
    component: GenericPieChartWidget,
    label: "Generic Pie Chart",
    description: "Donut / pie allocation chart from any endpoint",
    icon: PieChart,
    category: "charts",
    sizeCategory: "small",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 9, h: 8 },
    defaultConfig: {
      endpoint: "",
      title: "Allocation",
      name_field: "name",
      value_field: "value",
      donut: true,
      limit: 10,
      format: "number",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  generic_candlestick: {
    type: "generic_candlestick",
    component: GenericCandlestickWidget,
    label: "Generic Candlestick",
    description:
      "OHLCV candlestick chart with optional volume from any endpoint",
    icon: LineChart,
    category: "charts",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: {
      endpoint: "",
      title: "Price",
      timestamp_field: "timestamp",
      open_field: "open",
      high_field: "high",
      low_field: "low",
      close_field: "close",
      volume_field: "volume",
    },
    configurable: true,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SIGNALS — Analytical Widgets
  // Pairs trading visualizations: ratio, z-score, stationarity
  // ═══════════════════════════════════════════════════════════════════════════
  spread_ratio: {
    type: "spread_ratio",
    component: SpreadRatioWidget,
    label: "Spread & Ratio",
    description: "Price ratio (X/Y) with log-price toggle",
    icon: TrendingUp,
    category: "signals",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: { signal_name: "ratio" },
    configurable: false,
    hasInternalHeader: true,
  },

  zscore_engine: {
    type: "zscore_engine",
    component: ZScoreWidget,
    label: "Z-Score Engine",
    description:
      "Z-score oscillator with \u00B12\u03C3 entry and \u00B13\u03C3 extreme bands",
    icon: Activity,
    category: "signals",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: { signal_name: "z_score" },
    configurable: false,
    hasInternalHeader: true,
  },

  stationarity: {
    type: "hurst",
    component: StationarityWidget,
    label: "Hurst",
    description: "ADF p-value + Hurst exponent with trust thresholds",
    icon: FlaskConical,
    category: "signals",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    maxSize: { w: 12, h: 8 },
    defaultConfig: { hurst_signal: "hurst", adf_signal: "sig_adf_pvalue" },
    configurable: false,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TOKEN DASHBOARD WIDGETS (tw_*)
  // Per-widget token selection — each widget is independently configured
  // ═══════════════════════════════════════════════════════════════════════════

  // — Overview —
  tw_key_stats: {
    type: "tw_key_stats",
    component: TwKeyStatsWidget,
    label: "Token Key Stats",
    description: "Key statistics for a token (market cap, volume, TVL, fees)",
    icon: LayoutDashboard,
    category: "token_overview",
    sizeCategory: "small",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_performance_strip: {
    type: "tw_performance_strip",
    component: TwPerformanceStripWidget,
    label: "Token Performance Strip",
    description: "Price performance across multiple time windows",
    icon: TrendingUp,
    category: "token_overview",
    sizeCategory: "full",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 6, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_risk_efficiency: {
    type: "tw_risk_efficiency",
    component: TwRiskEfficiencyWidget,
    label: "Token Risk & Efficiency",
    description: "Risk-adjusted performance metrics (Sharpe, Sortino, Calmar)",
    icon: Activity,
    category: "token_overview",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_market_relationships: {
    type: "tw_market_relationships",
    component: TwMarketRelationshipsWidget,
    label: "Token Market Relationships",
    description: "Beta, correlation, and capture ratios vs BTC and ETH",
    icon: LineChart,
    category: "token_overview",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_sector_heat: {
    type: "tw_sector_heat",
    component: TwSectorHeatWidget,
    label: "Token Sector Heat",
    description: "Sector heatmap context for a token",
    icon: Layers,
    category: "token_overview",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_funding_trend: {
    type: "tw_funding_trend",
    component: TwFundingTrendWidget,
    label: "Token Funding Trend",
    description: "Historical funding rate trend for a token",
    icon: Percent,
    category: "token_overview",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_order_book: {
    type: "tw_order_book",
    component: TwOrderBookWidget,
    label: "Token Order Book",
    description: "Real-time Binance order book depth",
    icon: Scale,
    category: "token_overview",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_supply_unlocks: {
    type: "tw_supply_unlocks",
    component: TwSupplyUnlocksWidget,
    label: "Token Supply Unlocks",
    description: "Token allocation and upcoming unlock schedule",
    icon: Workflow,
    category: "token_overview",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
    defaultConfig: { ticker: "", tokenId: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  // — Performance —
  tw_moving_averages: {
    type: "tw_moving_averages",
    component: TwMovingAveragesWidget,
    label: "Token Moving Averages",
    description: "SMA lines and MA distance indicators",
    icon: LineChart,
    category: "token_performance",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_vs_benchmarks: {
    type: "tw_vs_benchmarks",
    component: TwVsBenchmarksWidget,
    label: "Token vs Benchmarks",
    description: "Indexed returns vs BTC and ETH",
    icon: LineChart,
    category: "token_performance",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_performance_quality: {
    type: "tw_performance_quality",
    component: TwPerformanceQualityWidget,
    label: "Token Performance Quality",
    description: "Alpha, capture ratios, and win rates vs benchmark",
    icon: Activity,
    category: "token_performance",
    sizeCategory: "wide",
    defaultSize: { w: 6, h: 4 },
    minSize: { w: 6, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_seasonal_performance: {
    type: "tw_seasonal_performance",
    component: TwSeasonalPerformanceWidget,
    label: "Token Seasonal Performance",
    description: "Monthly, quarterly, and annual return heatmap",
    icon: Calendar,
    category: "token_performance",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    defaultConfig: { ticker: "", tokenId: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_streaks_efficiency: {
    type: "tw_streaks_efficiency",
    component: TwStreaksEfficiencyWidget,
    label: "Token Streaks & Efficiency",
    description: "Win/loss streaks, CAGR, max drawdown, Calmar ratio",
    icon: Zap,
    category: "token_performance",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  // — Risk —
  tw_rolling_volatility: {
    type: "tw_rolling_volatility",
    component: TwRollingVolatilityWidget,
    label: "Token Rolling Volatility",
    description: "Rolling annualised volatility chart",
    icon: Activity,
    category: "token_risk",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_sharpe_sortino: {
    type: "tw_sharpe_sortino",
    component: TwSharpeSortinoWidget,
    label: "Token Sharpe / Sortino",
    description: "Rolling Sharpe and Sortino ratio chart",
    icon: Activity,
    category: "token_risk",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_var_es: {
    type: "tw_var_es",
    component: TwVarEsWidget,
    label: "Token VaR & ES",
    description: "Rolling Value-at-Risk and Expected Shortfall",
    icon: AlertTriangle,
    category: "token_risk",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_skew_kurtosis: {
    type: "tw_skew_kurtosis",
    component: TwSkewKurtosisWidget,
    label: "Token Skew & Kurtosis",
    description: "Rolling return distribution skewness and kurtosis",
    icon: Activity,
    category: "token_risk",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_upside_downside: {
    type: "tw_upside_downside",
    component: TwUpsideDownsideWidget,
    label: "Token Upside / Downside Deviation",
    description: "Rolling upside and downside deviation analysis",
    icon: Activity,
    category: "token_risk",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_tail_metrics: {
    type: "tw_tail_metrics",
    component: TwTailMetricsWidget,
    label: "Token Tail Metrics",
    description: "Tail risk panel: VaR, ES, skew, kurtosis, crash sensitivity",
    icon: AlertTriangle,
    category: "token_risk",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_beta_correlation: {
    type: "tw_beta_correlation",
    component: TwBetaCorrelationWidget,
    label: "Token Beta & Correlation",
    description:
      "Rolling beta, correlation, and upside/downside capture vs BTC/ETH",
    icon: LineChart,
    category: "token_risk",
    sizeCategory: "wide",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 6, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  // — Valuation —
  tw_fees_dynamics: {
    type: "tw_fees_dynamics",
    component: TwFeesDynamicsWidget,
    label: "Token Fees Dynamics",
    description: "Historical protocol fees time series",
    icon: DollarSign,
    category: "token_valuation",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_tvl_dynamics: {
    type: "tw_tvl_dynamics",
    component: TwTvlDynamicsWidget,
    label: "Token TVL Dynamics",
    description: "Total Value Locked history chart",
    icon: DollarSign,
    category: "token_valuation",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_dau_dynamics: {
    type: "tw_dau_dynamics",
    component: TwDauDynamicsWidget,
    label: "Token DAU Dynamics",
    description: "Daily Active Users history chart",
    icon: Users,
    category: "token_valuation",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_mc_fees_history: {
    type: "tw_mc_fees_history",
    component: TwMcFeesHistoryWidget,
    label: "Token MC/Fees History",
    description: "Market Cap to Fees ratio over time",
    icon: LineChart,
    category: "token_valuation",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_mc_tvl_history: {
    type: "tw_mc_tvl_history",
    component: TwMcTvlHistoryWidget,
    label: "Token MC/TVL History",
    description: "Market Cap to TVL ratio over time",
    icon: LineChart,
    category: "token_valuation",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_efficiency_panel: {
    type: "tw_efficiency_panel",
    component: TwEfficiencyPanelWidget,
    label: "Token Efficiency Panel",
    description: "Fee yield, Fees/TVL, and MC/Fees z-score",
    icon: Gauge,
    category: "token_valuation",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_valuation_insights: {
    type: "tw_valuation_insights",
    component: TwValuationInsightsWidget,
    label: "Token Valuation Insights",
    description: "Fee CV, MC/Fees momentum, and sector percentile ranks",
    icon: Brain,
    category: "token_valuation",
    sizeCategory: "wide",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 6, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_valuation_ratios: {
    type: "tw_valuation_ratios",
    component: TwValuationRatiosWidget,
    label: "Token Valuation Ratios",
    description: "MC/TVL, MC/Fees, MC/DAU, TVL/Fees, and other key ratios",
    icon: Scale,
    category: "token_valuation",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  // — Derivatives —
  tw_oi_summary: {
    type: "tw_oi_summary",
    component: TwOiSummaryWidget,
    label: "Token OI Summary",
    description: "Open interest snapshot with change windows",
    icon: BarChart3,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_oi_chart: {
    type: "tw_oi_chart",
    component: TwOiChartWidget,
    label: "Token OI Chart",
    description: "Historical open interest chart",
    icon: BarChart3,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "", tokenId: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_funding_summary: {
    type: "tw_funding_summary",
    component: TwFundingSummaryWidget,
    label: "Token Funding Summary",
    description: "Funding rate summary with period averages and extremes",
    icon: Percent,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 4 },
    minSize: { w: 3, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_weighted_funding_chart: {
    type: "tw_weighted_funding_chart",
    component: TwWeightedFundingChartWidget,
    label: "Token Weighted Funding Chart",
    description: "OI-weighted and volume-weighted funding rate history",
    icon: LineChart,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_exchange_funding: {
    type: "tw_exchange_funding",
    component: TwExchangeFundingWidget,
    label: "Token Exchange Funding",
    description: "Funding rate breakdown by exchange",
    icon: BarChart3,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_regime: {
    type: "tw_regime",
    component: TwRegimeWidget,
    label: "Token Funding Regime",
    description: "Current market regime based on funding and OI",
    icon: Gauge,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.QUARTER_SHORT,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_funding_extremes: {
    type: "tw_funding_extremes",
    component: TwFundingExtremesWidget,
    label: "Token Funding Extremes",
    description: "Historical funding rate extremes and distribution",
    icon: AlertTriangle,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  tw_funding_rate_chart: {
    type: "tw_funding_rate_chart",
    component: TwFundingRateChartWidget,
    label: "Token Funding Rate Chart",
    description: "Funding rate history chart",
    icon: LineChart,
    category: "token_derivatives",
    sizeCategory: "medium",
    defaultSize: WIDGET_SIZES.HALF_TALL,
    minSize: { w: 3, h: 4 },
    defaultConfig: { ticker: "" },
    configurable: true,
    hasInternalHeader: true,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SIGNALS — Sparkline Widgets
  // Individual signal sparkline charts (auto-generated)
  // ═══════════════════════════════════════════════════════════════════════════
  ...SIGNAL_WIDGET_ENTRIES,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any as Record<WidgetType, WidgetDefinition>;

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

export function getWidgetDefinition(
  type: WidgetType,
): WidgetDefinition | undefined {
  return WIDGET_REGISTRY[type];
}

export function getWidgetsByCategory(
  category: WidgetCategory,
): WidgetDefinition[] {
  return Object.values(WIDGET_REGISTRY).filter((w) => w.category === category);
}

export function getAllWidgetCategories(): WidgetCategory[] {
  return [
    "thesis",
    "token_overview",
    "token_performance",
    "token_risk",
    "token_valuation",
    "token_derivatives",
    "market",
    "charts",
    "portfolio",
    "technical",
    "social",
    "derivatives",
    "onchain",
    "macro",
    "signals",
    "utility",
  ];
}

export const WIDGET_CATEGORY_LABELS: Record<WidgetCategory, string> = {
  market: "Market Intelligence",
  token_overview: "Token — Overview",
  token_performance: "Token — Performance",
  token_risk: "Token — Risk",
  token_valuation: "Token — Valuation",
  token_derivatives: "Token — Derivatives",
  charts: "Charts & Visualization",
  portfolio: "Portfolio",
  technical: "Technical Analysis",
  social: "Social & Sentiment",
  derivatives: "Derivatives",
  onchain: "BTC On-Chain & Cycle",
  macro: "Macro",
  signals: "Signals",
  utility: "Utility",
  thesis: "Thesis",
};
